import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
export default function NativeOperationControls({ operation, onContinue, onCancel, onResult, onError, onExport }) {
  const [pending, setPending] = useState({});
  const [tick, setTick] = useState(Date.now());
  const inFlight = useRef(new Set());
  const mounted = useRef(true);
  useLayoutEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const active = operation && ['pending', 'running'].includes(operation.status);
  const waiting = active && operation.stage === 'awaiting_operator';
  const deadline = Date.parse(operation?.result?.continueBefore || '');
  useEffect(() => {
    if (!waiting || !Number.isFinite(deadline)) return undefined;
    setTick(Date.now());
    const timer = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [waiting, deadline]);
  if (!operation || !active && !onExport) return null;
  const remaining = Math.max(0, Math.ceil((deadline - tick) / 1000));
  const run = async (action, work, update = true) => {
    if (inFlight.current.has(action)) return;
    inFlight.current.add(action); setPending((value) => ({ ...value, [action]: true }));
    try { const result = await work(); if (mounted.current && update) onResult(result); }
    catch (error) { if (mounted.current) onError(error); }
    finally { inFlight.current.delete(action); if (mounted.current) setPending((value) => ({ ...value, [action]: false })); }
  };
  return <div className="native-operation-controls" aria-label="TV operation controls">
    {waiting && <>
      <p>{operation.result?.assistance || 'Check the selected app and picture on this TV before continuing.'}</p>
      <p>Continue applies once to this operation. {Number.isFinite(deadline) ? remaining ? `${remaining} seconds remaining.` : 'The continuation window has expired.' : 'It expires after two minutes.'} It does not validate unattended playback.</p>
      <button type="button" disabled={pending.continue || pending.cancel || !operation.id || Number.isFinite(deadline) && !remaining} onClick={() => run('continue', onContinue)}>Picture ready — Continue</button>
    </>}
    {(operation.result?.digitsSent > 0 || operation.result?.uncertainKey) && <p role="status">
      {operation.result.digitsSent || 0} digit requests sent{operation.result.enterSent ? ' and Enter sent' : ''}.
      {operation.result.uncertainKey ? ' Another key may have arrived before its reply was lost.' : ''} Check the TV before a new attempt; no keys will be replayed.
    </p>}
    {active && <button type="button" disabled={pending.cancel || !operation.id} onClick={() => run('cancel', onCancel)}>Stop pending TV actions</button>}
    {onExport && <button type="button" disabled={pending.export || !operation.id} onClick={() => run('export', onExport, false)}>Export TV operation</button>}
  </div>;
}

export function downloadNativeOperationReport(report, id) {
  const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }));
  const link = document.createElement('a'); link.href = url; link.download = `suitestream-tv-operation-${id}.json`; link.click();
  // Let the browser consume the download URL before revoking it.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
